/// <amd-module name="typeahead/core/presentation/TypeaheadDropdownBuilder" />

import {IListItem, LabelTypeListItem} from "typeahead/core/IListItem";
import {ISortedDisplayGroupItem} from "typeahead/core/presentation/DisplayGroupHandler";
import {SuggestionRendererUtils} from "typeahead/core/presentation/SuggestionRendererUtils";
import {IEssSuggestionResult, ISuggestionResult} from "typeahead/core/Suggestion";

export interface ITypeaheadDropdownModel {
    ariaOwns?: string;
    closeText: string;
    resultsByDisplayGroup: ISortedDisplayGroupItem[];
}

/* tslint:disable-next-line: no-trailing-whitespace */
/* This class is used to build the typeahead suggestions html as per the following handlebar template -
  <ul id="{{ariaOwns}}" role="listbox" class="results">
  {{#each resultsByDisplayGroup}}
    <div class="display-group-results">
    {{#if label}}
        <li role="presentation"  tabindex="-1"
            class="display-group-name">
            <div role="option" class="details">{{label}}</div>
        </li>
        {{/if}}
        {{#each items}}
        {{#if label}}
        <li role="presentation"  tabindex="-1"
                    class="non-results-item">
            {{#if showAsHierarchy}}
            <div class="child-suggestion">
                <span class="child-suggestion-label">
                {{label}}</span>
            </div>
            {{/if}}
        </li>
        {{/if}}
        <li class="results-item" role="presentation">
            {{#if html}}
                {{{html}}}
            {{else}}
                <a href="#"
                    class="details"
                    data-value="{{regionNames.lastSearchName}}"
                    id="aria-option-{{index}}"
                    role="option" tabindex="-1"
                    aria-selected="false"
                    data-type="{{type}}"
                    data-id="{{gaiaId}}"
                    style="{{style}}"
                    onclick="return false;">
                {{{createListItem this}}}
                </a>
            {{/if}}
        </li>
        {{/each}}
    </div>
    {{/each}}
</ul>
<footer class="footer">
    <a class="close" href="#" tabindex='-1' aria-label='{{{closeText}}}'>
        {{{closeText}}}
        <span class="icon icon-close"></span>
    </a>
</footer>
*/
export class TypeaheadDropdownBuilder {

    public static getHtml(model: ITypeaheadDropdownModel): string {
        let styleTag: Element = document.createElement("style");
        styleTag.innerHTML = TypeaheadDropdownBuilder.style;

        let ulTag: Element = document.createElement("ul");
        ulTag.setAttribute("class", "results");
        ulTag.setAttribute("id", model.ariaOwns);
        ulTag.setAttribute("role", "listbox");
        if (model.resultsByDisplayGroup && model.resultsByDisplayGroup.length) {
            for (let i: number = 0; i < model.resultsByDisplayGroup.length; i++) {
                ulTag.appendChild(TypeaheadDropdownBuilder.getDisplayGroupResultDiv(model.resultsByDisplayGroup[i]));
            }
        }

        let tempDiv: Element = document.createElement("div");
        tempDiv.appendChild(styleTag);
        tempDiv.appendChild(ulTag);
        tempDiv.appendChild(this.getFooter(model.closeText));
        return tempDiv.innerHTML;
    }

    private static style: string = `       
        .autocomplete-dropdown .display-group-results:not(:first-of-type) {
            padding-top: 0.9230769230769231em;
            border-top: 0.07692307692307693em solid #c7c7c7;
        }
        .autocomplete-dropdown .display-group-results:not(:last-of-type) {
            padding-bottom: 0.9230769230769231em;
        }
        .autocomplete-dropdown li.display-group-name {
            list-style: none;
            padding: 0 0.92307692em;
        }
        .autocomplete-dropdown li.display-group-name div.details {
            color: #313131;
            padding: 0.46153846em;
        }
        .autocomplete-dropdown li.non-results-item div.child-suggestion {
            padding-left: 1.88235em;
        }
        .autocomplete-dropdown span.child-suggestion-label {
            padding-left: 1.5em;
            color: #313131;
        }
        .autocomplete-dropdown li.results-item {
            margin: 0 !important;           
        }
        .results-item.list-item {
            box-shadow: 0 0 3px 0 rgba(0,0,0,0.2) !important;
            background: white;
            padding: 0;
        }
        .results-item.list-item > a.details {
            height: 44px;
            padding-left: 18px;
            padding-top: 12px;
        }  
        .autocomplete-dropdown > footer > a.close.highlight {
            border: 1px solid #0087f5;   
            padding-bottom: 5px;
            padding-top: 5px;
        }        
    `;

    private static getFooter(closeText: string) {
        let footer: Element = document.createElement("footer");
        footer.setAttribute("class", "footer");
        let aTag: Element = document.createElement("a");
        aTag.setAttribute("aria-label", closeText);
        aTag.setAttribute("class", "close");
        aTag.setAttribute("href", "#");
        aTag.innerHTML = closeText + `<span class=\"icon icon-close\"></span>`;
        footer.appendChild(aTag);
        return footer;
    }

    private static getDisplayGroupResultDiv(displayGroupResult: ISortedDisplayGroupItem): Element {
        let div: Element = document.createElement("div");
        div.setAttribute("class", "display-group-results");
        if (displayGroupResult.label) {
            div.appendChild(TypeaheadDropdownBuilder.getDisplayGroupLabelListItem(displayGroupResult));
        }
        if (displayGroupResult.items && displayGroupResult.items.length) {
            for (let i: number = 0; i < displayGroupResult.items.length; i++) {
                let item: ISuggestionResult = displayGroupResult.items[i];
                if (item.label && item.showAsHierarchy) {
                    div.appendChild(TypeaheadDropdownBuilder.getLabelListItem(item));
                }
                div.appendChild(TypeaheadDropdownBuilder.getSuggestionListItem(item));
            }
        }
        return div;
    }

    private static getDisplayGroupLabelListItem(displayGroupResult: ISortedDisplayGroupItem): Element {
        let liTag: Element = document.createElement("li");
        liTag.setAttribute("class", "display-group-name");
        liTag.setAttribute("role", "presentation");
        liTag.setAttribute("tabindex", "-1");
        liTag.innerHTML = `<div role="option" class="details">` + displayGroupResult.label + `</div>`;
        return liTag;
    }

    private static getLabelListItem(sr: ISuggestionResult): Element {
        let liTag: Element = document.createElement("li");
        liTag.setAttribute("class", "non-results-item");
        liTag.setAttribute("role", "presentation");
        liTag.setAttribute("tabindex", "-1");
        liTag.innerHTML = `
            <div class="child-suggestion">
                <span class="child-suggestion-label">` +
                    sr.label +
                `</span>
            </div>`;
        return liTag;
    }

    private static getSuggestionListItem(li: IListItem): Element {
        let liTag: Element = document.createElement("li");
        if ((<ISuggestionResult> li).type === "LIST") {
            liTag.setAttribute("class", "results-item list-item");
        } else {
            liTag.setAttribute("class", "results-item");
        }
        liTag.setAttribute("role", "presentation");
        if ((<LabelTypeListItem> li).html) {
            liTag.innerHTML = (<LabelTypeListItem> li).html;
        } else {
            liTag.appendChild(TypeaheadDropdownBuilder.getSuggestionAnchorTag(<ISuggestionResult> li));
        }
        return liTag;
    }

    private static getSuggestionAnchorTag(sr: ISuggestionResult): Element {
        let aTag: Element = document.createElement("a");
        aTag.setAttribute("aria-selected", "false");
        aTag.setAttribute("class", "details");
        aTag.setAttribute("data-id", (<IEssSuggestionResult> sr).gaiaId);
        aTag.setAttribute("data-row", sr.index);
        aTag.setAttribute("data-type", sr.type);
        aTag.setAttribute("data-value", sr.regionNames.lastSearchName);
        aTag.setAttribute("href", "#");
        aTag.setAttribute("id", "aria-option-" + sr.index);
        // Avoids the browser to follow the href and in turn trigger popstate event (which causes LXSRP page reload)
        aTag.setAttribute("onclick", "return false;");
        aTag.setAttribute("role", "option");
        aTag.setAttribute("style", sr.style ? sr.style : "");
        aTag.setAttribute("tabindex", "-1");

        aTag.innerHTML = SuggestionRendererUtils.getHtml(sr);
        return aTag;
    }
}
